/**
 * NetworkClub Types
 * Types voor netwerkclub zoek- en verbindfunctionaliteit
 */

// Moderatie status voor clubs
export type ClubModerationStatus = 'pending' | 'approved' | 'rejected';

// Scope types voor werkgebied
export type ClubScope = 'lokaal' | 'regionaal' | 'nationaal' | 'internationaal';

export interface NetworkClub {
  id: string;
  name: string;
  description?: string;
  kvkNumber?: string;
  
  // Locatie (legacy - blijft voor backward compatibility)
  region: string;        // Primaire provincie (bijv. "Noord-Holland")
  city?: string;         // Stad (bijv. "Amsterdam")
  country: string;       // Primair land (bijv. "Nederland")
  
  // NIEUW: Uitgebreid werkgebied
  scope?: ClubScope;              // Bereik van de club
  activeRegions?: string[];       // Alle actieve provincies ["Noord-Holland", "Zuid-Holland"]
  activeCountries?: string[];     // Voor internationale clubs ["Nederland", "België"]
  
  // Contact & links
  website?: string;
  linkedin?: string;
  email?: string;
  phone?: string;
  
  // Visueel
  logoUrl?: string;
  coverImageUrl?: string;
  
  // Stats
  memberCount?: number;
  foundedYear?: number;
  
  // Metadata
  categories?: string[];   // bijv. ["Ondernemers", "Tech", "Marketing"]
  meetingFrequency?: string; // bijv. "Maandelijks", "Wekelijks"
  isVerified?: boolean;
  isKvkValidated?: boolean;
  
  // Moderatie
  moderationStatus: ClubModerationStatus;
  moderationNote?: string;      // Admin notitie bij rejection
  moderatedAt?: Date;
  moderatedBy?: string;         // Admin userId
  
  // Firebase
  createdAt?: Date;
  updatedAt?: Date;
  organizerId?: string;         // userId van de organizer
  organizerName?: string;       // Naam voor admin overzicht
  organizerEmail?: string;      // Email voor contact
  
  // ✅ NIEUW: Entity Linking & Visibility
  entityId?: string;            // Link naar entities collection
  type?: 'networkclub' | 'mastermind' | 'community';  // Type entity
  visibility?: 'public' | 'private';  // Open of besloten
  isActive?: boolean;            // Actief (pas bij 5+ leden)
  isVisible?: boolean;           // Zichtbaar in zoekfunctie (pas bij 5+ leden)
  minMembersRequired?: number;   // Minimum leden (5)
}

// =====================================================
// NETWERKCLUB NIEUWS
// =====================================================

export interface NetworkClubNews {
  id: string;
  clubId: string;
  clubName: string;
  clubLogoUrl?: string;
  
  // Content
  title: string;
  content: string;
  imageUrl?: string;
  linkUrl?: string;
  
  // Targeting (wie mag dit zien)
  targetScope: ClubScope;
  targetRegions: string[];      // Specifieke provincies
  targetCountries: string[];    // Specifieke landen
  
  // Metadata
  createdAt: Date;
  updatedAt?: Date;
  expiresAt?: Date;             // Optioneel: nieuws verloopt
  isActive: boolean;
  views: number;
  
  // Auteur
  authorId: string;
  authorName?: string;
}

export interface NetworkClubSearchFilters {
  searchTerm?: string;
  region?: string;
  city?: string;
  country?: string;
  category?: string;
  scope?: ClubScope;
}

// =====================================================
// NIEUWS FILTER TYPES
// =====================================================

export type NewsFilterTab = 'all' | 'international' | 'national' | 'myRegion';

export interface NewsFilterConfig {
  tab: NewsFilterTab;
  sortBy: 'recent' | 'relevance' | 'popularity';
}

// Nederlandse provincies
export const DUTCH_PROVINCES = [
  'Drenthe',
  'Flevoland',
  'Friesland',
  'Gelderland',
  'Groningen',
  'Limburg',
  'Noord-Brabant',
  'Noord-Holland',
  'Overijssel',
  'Utrecht',
  'Zeeland',
  'Zuid-Holland'
] as const;

export type DutchProvince = typeof DUTCH_PROVINCES[number];

// Club categorieën
export const CLUB_CATEGORIES = [
  'Ondernemers',
  'Startups',
  'Scale-ups',
  'Tech & IT',
  'Marketing & Communicatie',
  'Finance & Legal',
  'Creatieve sector',
  'Zorg & Welzijn',
  'Retail & Horeca',
  'Vastgoed',
  'Consultancy',
  'Vrouwelijke ondernemers',
  'Jong ondernemen',
  'ZZP & Freelance',
  'Import & Export',
  'Duurzaamheid'
] as const;

export type ClubCategory = typeof CLUB_CATEGORIES[number];

// =====================================================
// SCOPE & REGIO CONFIGURATIE
// =====================================================

export const SCOPE_OPTIONS: { value: ClubScope; label: string; description: string }[] = [
  { 
    value: 'lokaal', 
    label: 'Lokaal', 
    description: 'Actief in één stad of provincie' 
  },
  { 
    value: 'regionaal', 
    label: 'Regionaal', 
    description: 'Actief in meerdere provincies' 
  },
  { 
    value: 'nationaal', 
    label: 'Nationaal', 
    description: 'Actief in heel Nederland' 
  },
  { 
    value: 'internationaal', 
    label: 'Internationaal', 
    description: 'Actief over landsgrenzen heen' 
  }
];

// Voorgedefinieerde regio-groepen voor snelle selectie
export const REGION_GROUPS: Record<string, string[]> = {
  'Randstad': ['Noord-Holland', 'Zuid-Holland', 'Utrecht'],
  'Noord-Nederland': ['Groningen', 'Friesland', 'Drenthe'],
  'Oost-Nederland': ['Overijssel', 'Gelderland', 'Flevoland'],
  'Zuid-Nederland': ['Noord-Brabant', 'Limburg', 'Zeeland'],
};

// Beschikbare landen voor internationale clubs
export const AVAILABLE_COUNTRIES = [
  'Nederland',
  'België',
  'Duitsland',
  'Frankrijk',
  'Verenigd Koninkrijk',
  'Luxemburg',
  'Oostenrijk',
  'Zwitserland',
  'Spanje',
  'Italië',
  'Verenigde Staten',
  'Canada'
] as const;

export type AvailableCountry = typeof AVAILABLE_COUNTRIES[number];

// =====================================================
// NETWERKCLUB LEDEN & GASTEN
// =====================================================

/**
 * Leden (permanente leden van de club)
 * Data komt uit entityMembers collection
 */
export interface NetworkClubMember {
  id: string; // userId
  clubId: string;
  entityId: string;  // Link naar entities collection
  name: string;
  email: string;
  photoURL?: string;
  role: 'owner' | 'organizer' | 'board' | 'va' | 'member'; // member = gewoon lid
  /** Optionele functie/taak binnen de club, los van permissie-niveau */
  functie?: string;
  joinedAt: Date;
  status: 'active' | 'inactive';
}

/**
 * Gasten (aangemeld voor events/meetings)
 * Data komt uit networkClubGuests collection
 */
export interface NetworkClubGuest {
  id: string;
  clubId: string;
  entityId?: string;  // Optioneel: link naar entities
  eventId?: string;   // Optioneel: specifiek event
  name: string;
  email: string;
  phone?: string;
  company?: string;
  invitedBy: string;   // userId van uitnodiger
  invitedAt: Date;
  status: 'invited' | 'confirmed' | 'attended' | 'cancelled';
  notes?: string;
}

/**
 * Bezoekverzoek (voor bezoekers die club willen bezoeken)
 * Data komt uit clubVisitRequests collection
 */
export interface ClubVisitRequest {
  id: string;
  clubId: string;
  entityId: string;   // Link naar entities collection
  visitorId: string;  // User ID van bezoeker
  visitorName: string;
  visitorEmail: string;
  visitorPhotoURL?: string;
  visitDate: Date;     // Gewenste bezoekdatum
  visitTime?: string;  // Optioneel: tijd
  purpose: string;    // Reden van bezoek
  message?: string;   // Optionele boodschap
  notes?: string;    // Interne notities (alias voor message)
  company?: string;   // Bedrijf van bezoeker
  phone?: string;     // Telefoonnummer van bezoeker
  status: 'pending' | 'accepted' | 'declined' | 'cancelled';
  createdAt: Date;
  respondedAt?: Date;
  respondedBy?: string;      // User ID van beheerder
  responseMessage?: string;  // Bericht van beheerder
}

// Tab configuratie voor nieuws filter
export const NEWS_TAB_CONFIG: Record<NewsFilterTab, { icon: string; label: string }> = {
  all: { icon: '📋', label: 'Alles' },
  international: { icon: '🌍', label: 'Internationaal' },
  national: { icon: '🇳🇱', label: 'Nationaal' },
  myRegion: { icon: '📍', label: 'Mijn Regio' }
};

// Scope badge configuratie
export const SCOPE_BADGE_CONFIG: Record<ClubScope, { bg: string; text: string; icon: string }> = {
  internationaal: { bg: 'bg-purple-100', text: 'text-purple-700', icon: '🌍' },
  nationaal: { bg: 'bg-blue-100', text: 'text-blue-700', icon: '🇳🇱' },
  regionaal: { bg: 'bg-green-100', text: 'text-green-700', icon: '📍' },
  lokaal: { bg: 'bg-orange-100', text: 'text-orange-700', icon: '🏠' }
};
